import { IconComponent } from '../../../utils/makeIcon';
import { ServiceAddOn } from '../../types/Service';
import { ServicesType } from '../../types/ServiceInstance';
import {
    allDayIcon,
    breakfastIcon,
    brunchIcon,
    dinnerIcon,
    lunchIcon,
    snackIcon,
} from './DiningMenuIcons';

export const MAX_ORDER_QUANTITY = 20;

export const addonSignature = (addons: ServiceAddOn[]) =>
    addons
        .map((addon) => addon.id)
        .sort()
        .join('|');

export type DietTag = {
    key: string;
    label: string;
    className: string;
};

type RestrictionKey = keyof NonNullable<
    NonNullable<ServicesType['mealRestrictions']>['restrictions']
>;

/** Known keys only — ignores API typo/duplicate fields (e.g. flexeterian). */
const RESTRICTION_KEYS: RestrictionKey[] = [
    'clearLiquid',
    'diabetic',
    'flexitarian',
    'glutenIntolerant',
    'kosher',
    'lactoseIntolerant',
    'lactoVegetarian',
    'lowCarb',
    'lowSodium',
    'mechanicalSoft',
    'noFish',
    'noMeat',
    'noPork',
    'noShellfish',
    'nutAllergy',
    'pescaterian',
    'pureed',
    'thickenedLiquid',
    'vegan',
    'vegetarian',
];

const LABEL_OVERRIDES: Partial<Record<RestrictionKey, string>> = {
    pescaterian: 'Pescatarian',
};

export const formatRestrictionLabel = (restrictionKey: RestrictionKey): string => {
    if (LABEL_OVERRIDES[restrictionKey]) return LABEL_OVERRIDES[restrictionKey]!;
    return restrictionKey
        .split(/(?=[A-Z])/)
        .map((word) => word.replace(/intolerant/i, 'free'))
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const TAG_CLASS_BY_KEY: Record<RestrictionKey, string> = {
    clearLiquid: 'tag-neutral',
    diabetic: 'tag-neutral',
    flexitarian: 'tag-veg',
    glutenIntolerant: 'tag-gf',
    kosher: 'tag-neutral',
    lactoseIntolerant: 'tag-df',
    lactoVegetarian: 'tag-veg',
    lowCarb: 'tag-neutral',
    lowSodium: 'tag-ls',
    mechanicalSoft: 'tag-neutral',
    noFish: 'tag-nut',
    noMeat: 'tag-nut',
    noPork: 'tag-nut',
    noShellfish: 'tag-nut',
    nutAllergy: 'tag-nut',
    pescaterian: 'tag-veg',
    pureed: 'tag-neutral',
    thickenedLiquid: 'tag-neutral',
    vegan: 'tag-veg',
    vegetarian: 'tag-veg',
};

export const getDietTags = (item: ServicesType): DietTag[] => {
    const restrictions = item.mealRestrictions?.restrictions;
    if (!restrictions) return [];

    return RESTRICTION_KEYS.filter((key) => restrictions[key]).map((key) => ({
        key,
        label: formatRestrictionLabel(key),
        className: TAG_CLASS_BY_KEY[key] ?? 'tag-neutral',
    }));
};

export interface MealGroup {
    mealKey: string;
    items: ServicesType[];
}

export interface MealMeta {
    label: string;
    iconClass: string;
    Icon: IconComponent;
}

const MEAL_SORT_ORDER = ['breakfast', 'lunch', 'dinner', 'brunch', 'all day', 'snack'];

const MEAL_META: Record<string, MealMeta> = {
    breakfast: { label: 'Breakfast', iconClass: 'breakfast', Icon: breakfastIcon },
    brunch: { label: 'Brunch', iconClass: 'brunch', Icon: brunchIcon },
    lunch: { label: 'Lunch', iconClass: 'lunch', Icon: lunchIcon },
    snack: { label: 'Snack', iconClass: 'snack', Icon: snackIcon },
    dinner: { label: 'Dinner', iconClass: 'dinner', Icon: dinnerIcon },
    'all day': { label: 'All Day', iconClass: 'allday', Icon: allDayIcon },
};

export const MEAL_FILTER_OPTIONS = [
    { value: 'all', label: 'All Meals' },
    ...MEAL_SORT_ORDER.map((value) => ({
        value,
        label: MEAL_META[value]?.label ?? value,
    })),
];

export const getMealMeta = (mealKey: string): MealMeta =>
    MEAL_META[mealKey] ?? {
        label: mealKey.charAt(0).toUpperCase() + mealKey.slice(1),
        iconClass: 'allday',
        Icon: allDayIcon,
    };

const MIXABLE_MEAL_CATEGORIES = new Set(['snack', 'all day']);

/** Display/grouping — empty categories fall back to "all day" for UI bucketing. */
export const normalizeMealCategory = (category?: string): string =>
    (category || '').toLowerCase().trim() || 'all day';

const UNCATEGORIZED_MEAL_KEY = '__uncategorized__';

/** Meal-mix rules — only explicit snack/all day are permissive; empty stays strict. */
export const normalizeMealCategoryForMix = (category?: string): string => {
    const normalized = (category || '').toLowerCase().trim();
    return normalized || UNCATEGORIZED_MEAL_KEY;
};

const getMealCategoryLabelForMix = (category?: string): string => {
    const normalized = normalizeMealCategoryForMix(category);
    if (normalized === UNCATEGORIZED_MEAL_KEY) return 'Uncategorized';
    return getMealMeta(normalized).label;
};

/** Snack and All Day mix with everything; breakfast/brunch/lunch/dinner cannot mix with each other. */
export const findConflictingMealCategory = (
    incomingCategory: string | undefined,
    existingCategories: Array<string | undefined>,
): string | undefined => {
    const incoming = normalizeMealCategoryForMix(incomingCategory);
    if (MIXABLE_MEAL_CATEGORIES.has(incoming)) return undefined;

    return existingCategories
        .map(normalizeMealCategoryForMix)
        .find((existing) => !MIXABLE_MEAL_CATEGORIES.has(existing) && existing !== incoming);
};

export const mealMixErrorMessage = (incomingCategory?: string, conflictingCategory?: string): string => {
    const incomingLabel = getMealCategoryLabelForMix(incomingCategory);
    const conflictLabel = getMealCategoryLabelForMix(conflictingCategory);
    return `${incomingLabel} items cannot be ordered with a ${conflictLabel}.`;
};

export const groupByMeal = (items: ServicesType[]): MealGroup[] => {
    const groups = new Map<string, ServicesType[]>();

    items.forEach((item) => {
        const category = (item.menuCategory || '').toLowerCase().trim() || 'all day';
        const list = groups.get(category);
        if (Array.isArray(list)) {
            list.push(item);
        } else {
            groups.set(category, [item]);
        }
    });

    const knownKeys = MEAL_SORT_ORDER.filter((m) => groups.has(m));
    const unknownKeys = Array.from(groups.keys()).filter((k) => !MEAL_SORT_ORDER.includes(k));

    return [...knownKeys, ...unknownKeys].map((mealKey) => ({
        mealKey,
        items: groups.get(mealKey)!,
    }));
};
